/**
 * Client-side sorting for the Devices page's fully-drained device lists.
 *
 * Both the Activated and Fosters sections sort over the whole dataset rather than the
 * visible page, so the comparator is the only thing deciding row order — the server
 * orders by pet name and can't sort on any of these keys. Kept out of the page module
 * so the ordering rules can be exercised without standing up the page.
 */

import type { Device, DeviceSortKey, SortDir } from './types';

export interface DeviceSort {
  key: DeviceSortKey;
  dir: SortDir;
}

/** Sort key for the Placed On column, in ms since epoch; 0 when the row has no usable date. */
function placedAtMs(device: Device): number {
  if (!device.transferDate) {
    return 0;
  }
  const ms = new Date(device.transferDate).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Returns the list unchanged when no sort is active. Sorts a copy (never mutates the
 * input) so memoized upstream lists stay stable. `Array.prototype.sort` is stable, so
 * ties keep the server's pet-name order rather than shuffling between renders.
 */
export function sortDevices(list: Device[], sort: DeviceSort | null): Device[] {
  if (!sort) {
    return list;
  }
  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    let cmp: number;
    if (sort.key === 'holder') {
      cmp = (a.holderName ?? '').localeCompare(b.holderName ?? '');
    } else if (sort.key === 'pet') {
      cmp = (a.petName ?? '').localeCompare(b.petName ?? '');
    } else if (sort.key === 'placed') {
      // The placement date (`transferDate` on an IN_FOSTER row). A row without one reads
      // as epoch, so the handful that lack a resolvable transfer collect at the old end
      // rather than scattering through the dates that do sort.
      cmp = placedAtMs(a) - placedAtMs(b);
    } else {
      cmp = (a.id ?? '').localeCompare(b.id ?? '');
    }
    return cmp * mult;
  });
}
